import { Box, TextField, TextFieldProps } from '@mui/material';
import { useRef, useEffect, useState } from 'react';
import { IAddress } from 'components/types';

type InlineConfigBase<V> = {
  value: V;
  onChange: (v: V) => void;
  onCommit: (v: V) => Promise<boolean> | void;
  onCancel: () => void;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
};

type AddressInlineConfig = InlineConfigBase<IAddress>;

type AddressManagerProps = {
  address?: IAddress;
  setAddress?: (address: IAddress) => void;
  extraProps?: Partial<TextFieldProps>;
  inlineConfig?: AddressInlineConfig;
};

const AddressManager = ({ address = {}, setAddress, extraProps, inlineConfig }: AddressManagerProps) => {
  const isInline = !!inlineConfig;
  const streetRef = useRef<HTMLInputElement | null>(null);
  const [localAddress, setLocalAddress] = useState<IAddress>(() =>
    isInline ? { ...(inlineConfig?.value || {}) } : { ...address },
  );
  const localAddressRef = useRef(localAddress);
  localAddressRef.current = localAddress;

  useEffect(() => {
    if (isInline && inlineConfig?.autoFocus !== false) {
      const id = setTimeout(() => streetRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [isInline, inlineConfig?.autoFocus]);

  const handleChange = (field: keyof IAddress, fieldValue: string) => {
    const next = { ...localAddressRef.current, [field]: fieldValue } as IAddress;
    localAddressRef.current = next;
    setLocalAddress(next);

    if (isInline) {
      inlineConfig?.onChange(next);
    } else {
      setAddress?.(next);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (!isInline) return;
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      await inlineConfig?.onCommit?.(localAddressRef.current);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      inlineConfig?.onCancel();
    }
  };

  const buildProps = (field: keyof IAddress): Partial<TextFieldProps> => ({
    fullWidth: true,
    size: 'small',
    disabled: isInline ? inlineConfig?.disabled : extraProps?.disabled,
    error: isInline ? inlineConfig?.hasError : extraProps?.error,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(field, e.target.value),
    onKeyDown: handleKeyDown,
    value: (localAddress[field] as string) || '',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Street Address"
        inputRef={isInline ? streetRef : undefined}
        {...buildProps('street')}
        {...(isInline ? {} : extraProps)}
      />
      <TextField label="City" {...buildProps('city')} {...(isInline ? {} : extraProps)} />
      <TextField label="State" {...buildProps('state')} {...(isInline ? {} : extraProps)} />
    </Box>
  );
};

export default AddressManager;
